import { useState } from "react";
import { motion } from "framer-motion";
import { Phone } from "lucide-react";
import Navbar from "../components/Navbar";
import CalculatorSection from "../components/CalculatorSection";
import LocalityContentSection from "../components/LocalityContentSection";
import ServicesSection from "../components/ServicesSection";
import ProcessSection from "../components/ProcessSection";
import CoverageLinksSection from "../components/CoverageLinksSection";
import FooterSection from "../components/FooterSection";
import FloatingActions from "../components/FloatingActions";
import ChatWidget from "../components/ChatWidget";
import QuoteFormModal from "../components/QuoteFormModal";
import Seo from "../components/Seo";
import {
  DEFAULT_PRICING,
  findLocalityBySlug,
  localitySlug,
  PHONE_DISPLAY,
  PHONE_TEL,
  formatLei,
} from "../lib/api";

export default function LocalityPage({ slug }) {
  const loc = findLocalityBySlug(slug);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quotePrefill, setQuotePrefill] = useState(null);
  const openQuote = (prefill = null) => {
    setQuotePrefill(prefill);
    setQuoteOpen(true);
  };

  if (!loc) {
    return (
      <div className="min-h-screen bg-hydro-navy font-body grid place-items-center px-5 text-center">
        <Seo
          title="Pagina nu există | Puțul tău"
          description="Localitatea căutată nu e în lista noastră de acoperire. Vezi toate localitățile din București-Ilfov unde forăm puțuri de apă."
        />
        <div>
          <h1 className="font-display text-4xl font-extrabold text-white">Localitatea nu e în listă.</h1>
          <p className="mt-3 text-white/60">Sună-ne oricum la {PHONE_DISPLAY} — acoperim tot București-Ilfov.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            {DEFAULT_PRICING.localities.slice(0, 12).map((l) => (
              <a
                key={l.name}
                href={`/foraje-puturi/${localitySlug(l.name)}`}
                className="rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-white/70 hover:border-hydro-cyan hover:text-hydro-cyan"
              >
                {l.name}
              </a>
            ))}
          </div>
          <a href="/" className="mt-8 inline-block text-sm font-bold text-hydro-cyan hover:text-white">
            ← Înapoi la pagina principală
          </a>
        </div>
      </div>
    );
  }

  const suffix = loc.group === "Ilfov" ? ", Ilfov" : "";
  const title = `Foraje Puțuri ${loc.name}${suffix} — Calculator Preț Online`;
  const description = `Forăm puțuri de apă în ${loc.name}${suffix}: țeavă PVC albastră cu filet, denisipări, montaj pompe. Preț orientativ calculat online — sună la ${PHONE_DISPLAY}.`;

  return (
    <div id="top" className="bg-hydro-navy text-slate-100 font-body antialiased overflow-x-clip">
      <Seo
        title={title}
        description={description}
        canonical={`https://forajrapid.ro/foraje-puturi/${slug}`}
      />
      <Navbar />
      <main>
        <section className="relative overflow-hidden bg-hydro-navy px-5 pt-32 pb-16 sm:px-8">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-24 right-0 h-72 w-72 rounded-full bg-hydro-blue/20 blur-3xl"
          />
          <div className="mx-auto max-w-7xl">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7 }}
              className="font-mono text-[11px] uppercase tracking-[0.32em] text-hydro-cyan sm:text-xs"
            >
              Foraje puțuri de apă · {loc.group}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="mt-5 max-w-3xl font-display text-4xl font-black leading-[1.0] tracking-tight text-white sm:text-6xl"
            >
              Foraje puțuri în <span className="text-hydro-cyan">{loc.name}</span>{suffix}.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="mt-5 max-w-xl text-base leading-relaxed text-white/65"
            >
              Echipa Puțul tău forază în {loc.name} și în tot județul Ilfov, cu tubulatură PVC
              albastră cu filet. Prețul orientativ se calculează mai jos, în 30 de secunde.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="mt-7 flex flex-wrap items-center gap-3"
            >
              <span
                data-testid="locality-transport-fact"
                className="rounded-full border border-hydro-cyan/40 bg-hydro-cyan/10 px-4 py-2 font-mono text-xs font-bold text-hydro-cyan"
              >
                Deplasare în {loc.name}:{" "}
                {loc.transport > 0 ? `${formatLei(loc.transport)} lei` : "inclusă"}
              </span>
              <span className="rounded-full border border-white/15 px-4 py-2 font-mono text-xs text-white/60">
                Adâncimi 10–150 m
              </span>
              <a
                href={PHONE_TEL}
                data-testid="locality-call-button"
                className="inline-flex items-center gap-2 rounded-full bg-hydro-cyan px-5 py-2 text-xs font-extrabold text-hydro-navy transition-colors hover:bg-white"
              >
                <Phone size={13} />
                {PHONE_DISPLAY}
              </a>
            </motion.div>
          </div>
        </section>

        <CalculatorSection defaultLocality={loc.name} onOpenQuote={openQuote} />
        <LocalityContentSection locality={loc} />
        <ServicesSection />
        <ProcessSection />
        <CoverageLinksSection currentSlug={slug} />
      </main>
      <FooterSection onOpenQuote={openQuote} />
      <FloatingActions />
      <ChatWidget />
      <QuoteFormModal open={quoteOpen} onOpenChange={setQuoteOpen} prefill={quotePrefill} />
    </div>
  );
}
